import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  HeaderConfig,
  ManageEntitiesContentLayout
} from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { archivedFilter } from '../../../filters/archivedFilter/archivedFilter';
import { constructionSiteTextFilter } from '../../../filters/constructionSiteTextFilter/constructionSiteTextFilter';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useUserGroupCreateEntityButtonConfig } from '../../../hooks/entity/useUserGroupCreateEntityButtonConfig';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { ConstructionSiteList } from '../../constructionSite/ConstructionSiteList/ConstructionSiteList';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

export type ConstructionSiteAdminPageProps = PropsWithoutChildren<
  Record<never, never>
>;

export const ConstructionSiteAdminPage: FunctionComponent<ConstructionSiteAdminPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    const navigate = useNavigate();
    const { t } = useTranslation();
    const { getRequiredCurrentUserPermissions } = useCurrentUser();

    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder.addHomePage().addConstructionSiteAdminPage()
    );

    const constructionSites = useEntities(
      EntityName.ConstructionSite,
      'getAll'
    );

    const createConstructionSite = useCallback(
      (userGroup: UserGroup | null) => {
        assertNotNullOrUndefined(
          userGroup,
          "can't create a ConstructionSite without a usergroup"
        );
        navigate(
          RoutePaths.adminConstructionSitesCreateConstructionSite(userGroup.id)
        );
      },
      [navigate]
    );

    const headerConfig: HeaderConfig = useMemo(() => {
      return {
        iconConfig:
          iconConfigurations.prefixed.page.ConstructionSiteAdminPage.titleIcon,
        title: t('components.page.ConstructionSiteAdminPage.title')
      };
    }, [t]);

    const renderEntities = useCallback(
      (currentPageConstructionSites: Array<ConstructionSite>) => {
        return (
          <ConstructionSiteList
            constructionSites={currentPageConstructionSites}
            data-testid="ConstructionSiteAdminPage.ConstructionSiteList"
          ></ConstructionSiteList>
        );
      },
      []
    );

    const createEntityButtonConfig = useUserGroupCreateEntityButtonConfig({
      text: t('components.page.ConstructionSiteAdminPage.create'),
      userGroups:
        getRequiredCurrentUserPermissions().getEditableUserGroupsForConstructionSites(),
      onCreateEntity: createConstructionSite
    });

    const filters = useMemo(() => {
      return [constructionSiteTextFilter, archivedFilter];
    }, []);

    return (
      <ManageEntitiesContentLayout<ConstructionSite, UserGroup>
        headerConfig={headerConfig}
        createEntityButtonConfig={createEntityButtonConfig}
        entities={constructionSites}
        renderEntities={renderEntities}
        filters={filters}
        data-testid="ConstructionSiteAdminPage.ManageEntitiesContentLayout"
      ></ManageEntitiesContentLayout>
    );
  });
